import React from 'react';

/**
 * RadioButtonGroup - A modern segmented control component
 * 
 * @param {Array} options - Array of option objects with { key, label, icon, color }
 * @param {String} activeKey - Currently selected option key
 * @param {Function} onChange - Callback function when selection changes
 * @param {String} size - Size variant: 'small', 'medium', 'large' (default: 'medium')
 * @param {Object} containerStyle - Additional styles for the container
 */
const RadioButtonGroup = ({ 
  options = [], 
  activeKey, 
  onChange, 
  size = 'medium',
  containerStyle = {}
}) => {
  
  // Size configurations
  const sizes = {
    small: {
      padding: '6px 12px',
      fontSize: '13px',
      height: '28px',
      minWidth: '80px',
      iconSize: '10px',
      containerPadding: '3px'
    },
    medium: {
      padding: '10px 16px',
      fontSize: '15px',
      height: '36px',
      minWidth: '120px',
      iconSize: '12px',
      containerPadding: '4px'
    },
    large: {
      padding: '12px 20px',
      fontSize: '17px',
      height: '44px',
      minWidth: '140px',
      iconSize: '14px',
      containerPadding: '5px'
    }
  };

  const sizeConfig = sizes[size] || sizes.medium;

  return (
    <div 
      className="radio-button-group" 
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '12px',
        padding: sizeConfig.containerPadding,
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        gap: '2px',
        ...containerStyle
      }}
    >
      {options.map((option) => (
        <button
          key={option.key}
          onClick={() => onChange(option.key)}
          title={option.tooltip || option.label}
          disabled={option.disabled}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: sizeConfig.padding,
            border: 'none',
            background: activeKey === option.key
              ? 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
              : 'transparent',
            color: activeKey === option.key ? '#1e293b' : '#64748b',
            fontSize: sizeConfig.fontSize,
            fontWeight: activeKey === option.key ? '600' : '500',
            cursor: option.disabled ? 'not-allowed' : 'pointer',
            opacity: option.disabled ? 0.5 : 1,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: '8px',
            position: 'relative',
            gap: '8px',
            minWidth: sizeConfig.minWidth,
            height: sizeConfig.height,
            boxShadow: activeKey === option.key
              ? '0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1)'
              : 'none',
            transform: activeKey === option.key ? 'translateY(-1px)' : 'translateY(0)',
            border: activeKey === option.key
              ? `1px solid ${option.color || '#3b82f6'}20`
              : '1px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (activeKey !== option.key && !option.disabled) {
              e.target.style.background = '#f1f5f9';
              e.target.style.color = option.color || '#3b82f6';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeKey !== option.key && !option.disabled) {
              e.target.style.background = 'transparent';
              e.target.style.color = '#64748b';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }
          }}
          onMouseDown={(e) => {
            if (!option.disabled) {
              e.target.style.transform = 'translateY(0) scale(0.98)';
            }
          }}
          onMouseUp={(e) => {
            if (!option.disabled) {
              e.target.style.transform = activeKey === option.key
                ? 'translateY(-1px) scale(1)'
                : 'translateY(0) scale(1)';
            }
          }}
        >
          {option.icon && (
            <div style={{
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: activeKey === option.key
                ? `${option.color || '#3b82f6'}15`
                : 'transparent',
              borderRadius: '6px',
              transition: 'all 0.3s ease'
            }}>
              <i className={`fa-solid ${option.icon}`} style={{
                fontSize: sizeConfig.iconSize,
                color: activeKey === option.key ? (option.color || '#3b82f6') : '#64748b',
                transition: 'all 0.3s ease'
              }}></i>
            </div>
          )}
          <span style={{
            fontSize: sizeConfig.fontSize,
            fontWeight: activeKey === option.key ? '600' : '500',
            letterSpacing: '0.3px'
          }}>
            {option.label}
          </span>
          {option.badge && (
            <span style={{
              backgroundColor: option.badgeColor || '#ef4444',
              color: '#ffffff',
              fontSize: '10px',
              fontWeight: '600',
              padding: '2px 6px',
              borderRadius: '10px',
              marginLeft: '-2px'
            }}>
              {option.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

export default RadioButtonGroup;

